// Month arithmetic for the monthly SLA compliance report. A month is identified by its key,
// "YYYY-MM" — the same token the Infrastructure file is named after.
//
// Boundaries are IST (+05:30). India observes no DST, so a constant offset is exact and works
// identically in the browser, in the PDF worker and on the server.

const OFFSET_MS = 330 * 60 * 1000;

/**
 * The earliest month any tenant has a published Infrastructure file for — `cube` goes back this far
 * (censused against the bucket: cube 2026-01..2026-08, mp 2026-04.., bpcl 2026-06..).
 *
 * It is deliberately the earliest across ALL tenants rather than per-tenant: the picker gates both
 * halves of the report, the Incident half has its own longer history, and a month with no file for
 * the chosen tenant is a rendered "not published" notice, not an error. Offering it and saying so
 * beats hiding a month whose Incident half is perfectly exportable.
 */
export const SLA_REPORT_FIRST_MONTH = "2026-01";

export type MonthKey = string;

export const isMonthKey = (value: unknown): value is MonthKey =>
  typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);

export function parseMonthKey(key: string): { year: number; month: number } | null {
  if (!isMonthKey(key)) return null;
  return { year: Number(key.slice(0, 4)), month: Number(key.slice(5, 7)) };
}

/** `month` is 1-based and may overflow, so 13 rolls into the next year. */
export function monthKeyOf(year: number, month: number): MonthKey {
  const y = year + Math.floor((month - 1) / 12);
  const m = ((((month - 1) % 12) + 12) % 12) + 1;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}`;
}

export function monthKeyAt(epochMs: number): MonthKey {
  const shifted = new Date(epochMs + OFFSET_MS);
  return monthKeyOf(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1);
}

export function addMonths(key: MonthKey, delta: number): MonthKey {
  const parsed = parseMonthKey(key);
  return parsed ? monthKeyOf(parsed.year, parsed.month + delta) : key;
}

export const compareMonthKeys = (a: MonthKey, b: MonthKey): number => (a < b ? -1 : a > b ? 1 : 0);

/** Inclusive at both ends — `endMs` is handed to the grid filter's `lessthanorequal`. */
export function monthRangeMs(key: MonthKey): { startMs: number; endMs: number } | null {
  const parsed = parseMonthKey(key);
  if (!parsed) return null;
  return {
    startMs: Date.UTC(parsed.year, parsed.month - 1, 1) - OFFSET_MS,
    endMs: Date.UTC(parsed.year, parsed.month, 1) - OFFSET_MS - 1,
  };
}

export function daysInMonth(key: MonthKey): number {
  const parsed = parseMonthKey(key);
  return parsed ? new Date(Date.UTC(parsed.year, parsed.month, 0)).getUTCDate() : 0;
}

export const minutesInMonth = (key: MonthKey): number => daysInMonth(key) * 24 * 60;

/** `June 2026`. */
export function monthLabel(key: MonthKey, locale = "en-GB"): string {
  const parsed = parseMonthKey(key);
  if (!parsed) return key;
  return new Date(Date.UTC(parsed.year, parsed.month - 1, 1)).toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** `01 June 2026 to 30 June 2026`. */
export function monthRangeLabel(key: MonthKey, locale = "en-GB"): string {
  const parsed = parseMonthKey(key);
  if (!parsed) return key;
  const format = (day: number) =>
    new Date(Date.UTC(parsed.year, parsed.month - 1, day)).toLocaleDateString(locale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  return `${format(1)} to ${format(daysInMonth(key))}`;
}

export const latestClosedMonth = (nowMs: number): MonthKey => addMonths(monthKeyAt(nowMs), -1);

/**
 * Every month the picker may offer, newest first. Only fully-elapsed months: a partial month would
 * print a period it does not cover and divide its uptime by the wrong number of minutes.
 */
export function selectableReportMonths(nowMs: number, firstMonth: MonthKey = SLA_REPORT_FIRST_MONTH): MonthKey[] {
  const newest = latestClosedMonth(nowMs);
  if (!isMonthKey(firstMonth) || compareMonthKeys(firstMonth, newest) > 0) return [];
  const months: MonthKey[] = [];
  for (let key = newest; compareMonthKeys(key, firstMonth) >= 0; key = addMonths(key, -1)) {
    months.push(key);
  }
  return months;
}

// ── Calendar-widget interop ──────────────────────────────────────────────────
//
// ⚠️ These two are LOCAL-time on purpose, and are the only functions in this file that are. Every
// other boundary here is IST, because it describes the period the report covers. A month/year
// picker describes nothing — it renders whatever `Date` it is handed in the BROWSER's zone, so a
// UTC-midnight 1st of the month would draw as the last day of the previous month for any viewer
// west of Greenwich, and the picker would disagree with its own label. Building and reading the
// date in local time makes the round trip exact in every zone.

/** The 1st of `key` as a local-time `Date`, for handing to a month/year picker. */
export function monthKeyToLocalDate(key: MonthKey): Date | null {
  const parsed = parseMonthKey(key);
  return parsed ? new Date(parsed.year, parsed.month - 1, 1) : null;
}

/** The month a picker's `Date` falls in, read in local time. Inverse of {@link monthKeyToLocalDate}. */
export function localDateToMonthKey(date: Date): MonthKey | null {
  if (Number.isNaN(date.getTime())) return null;
  return monthKeyOf(date.getFullYear(), date.getMonth() + 1);
}
